import React, { useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Stack,
    Typography,
    IconButton,
    Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

const enum IPTypeEnum {
    BLACKLIST = 1,
    WHITELIST = 2
}

const ipControlSchema = z.object({
    id: z.number().optional(),
    ipAddress: z.string().min(1, "IP Adresi gereklidir").ip({ message: "Geçersiz IP Adresi formatı" }),
    description: z.string().optional(),
    type: z.enum([IPTypeEnum.WHITELIST.toString(), IPTypeEnum.BLACKLIST.toString()]),
    merchantId: z.union([z.string(), z.number()]).nullable()
})
    .refine(data => {
        if (data.type === IPTypeEnum.WHITELIST.toString() && !data.merchantId) {
            return false
        }
        return true;
    }, { message: "Lütfen bir işyeri seçin", path: ["merchantId"] });

export type IpControlFormValues = z.infer<typeof ipControlSchema>;

interface IPControlModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: IpControlFormValues) => void;
    initialData?: any;
    merchantList: any[]
}

export const IPControlModal: React.FC<IPControlModalProps> = ({
    isOpen,
    onClose,
    onSave,
    initialData,
    merchantList
}) => {
    const isEditMode = !!initialData?.id;

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
        watch
    } = useForm<IpControlFormValues>({
        resolver: zodResolver(ipControlSchema),
        defaultValues: {
            ipAddress: "",
            description: "",
            type: "whitelist",
            merchantId: null
        },
    });

    const watchType = watch("type");
    const watchMerchantId = watch("merchantId");

    useEffect(() => {
        console.log('here is merchant id', watchMerchantId);
    }, [watchMerchantId]);

    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                reset({
                    id: initialData.id,
                    ipAddress: initialData.ip || "",
                    description: initialData.description || "",
                    type: initialData.ruleType || IPTypeEnum.WHITELIST.toString(),
                    merchantId: initialData.merchantId || null
                });
            } else {
                reset({
                    ipAddress: "",
                    description: "",
                    type: IPTypeEnum.WHITELIST.toString(),
                    merchantId: null
                });
            }
        }
    }, [isOpen, initialData, reset]);

    const onSubmit = (data: IpControlFormValues) => {
        onSave(data);
    };

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "12px" } }}>
            <DialogTitle sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" component="div">
                        {isEditMode ? "IP Kuralını Düzenle" : "Yeni IP Kuralı Ekle"}
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
                <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={2}>
                    <Controller
                        name="ipAddress"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                autoFocus
                                label="IP Adresi *"
                                fullWidth
                                variant="outlined"
                                error={!!errors.ipAddress}
                                helperText={errors.ipAddress?.message || "Geçerli bir IPv4 adresi girin."}
                            />
                        )}
                    />
                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Açıklama"
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                            />
                        )}
                    />
                    {watchType === IPTypeEnum.WHITELIST.toString() && (
                        <Controller
                            name="merchantId"
                            control={control}
                            render={({ field: { onChange, value } }) => {
                                const selectedMerchant = merchantList.find(
                                    (option) => option.value === value
                                ) || null;

                                return (
                                    <Autocomplete
                                        id="merchantId-select"
                                        options={merchantList}
                                        getOptionLabel={(option) => option.label || ''}
                                        value={selectedMerchant}
                                        onChange={(_, newValue) => {
                                            onChange(newValue ? newValue.value : "");
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Üye İşyeri"
                                                error={!!errors.merchantId}
                                                helperText={errors.merchantId?.message}
                                            />
                                        )}
                                    />
                                );
                            }}
                        />
                    )}
                    <FormControl component="fieldset" disabled={isEditMode}>
                        <FormLabel component="legend">Kural Tipi</FormLabel>
                        <Controller
                            name="type"
                            control={control}
                            render={({ field }) => (
                                <RadioGroup {...field} row>
                                    <FormControlLabel
                                        value="2"
                                        control={<Radio />}
                                        label="Whitelist (İzin Ver)"
                                    />
                                    <FormControlLabel
                                        value="1"
                                        control={<Radio />}
                                        label="Blacklist (Engelle)"
                                    />
                                </RadioGroup>
                            )}
                        />
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="outlined" color="secondary">
                    İptal
                </Button>
                <Button onClick={handleSubmit(onSubmit)} variant="contained" color="primary">
                    Kaydet
                </Button>
            </DialogActions>
        </Dialog>
    );
};
